import { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { PublicKey } from '@solana/web3.js';
import { useSnackbar } from 'notistack';
import DialogForm from '../DialogForm';
import strings from '../../localization';
import { PoolInfo, useChainClock, useClaim, useUnstake } from '../../utils/tokenStaking/hooks';
import { Position, claimableUnits, formatBps, formatUnits, toUnits } from '../../utils/tokenStaking/program';
import TokenStakeDialog, { formatDate } from './TokenStakeDialog';

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1) },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: theme.spacing(1) },
  actions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(1) },
}));

export interface PositionCardProps {
  mint: PublicKey;
  symbol: string;
  info: PoolInfo;
  position: Position;
}

export default function PositionCard({ mint, symbol, info, position }: PositionCardProps) {
  const t = strings.tokenStaking;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const clock = useChainClock();
  const claim = useClaim(info.pool);
  const unstake = useUnstake(info.pool);
  const [now, setNow] = useState(clock());
  const [stakeOpen, setStakeOpen] = useState(false);
  const [unstakeOpen, setUnstakeOpen] = useState(false);
  const [unstakeInput, setUnstakeInput] = useState('');

  useEffect(() => {
    const id = setInterval(() => setNow(clock()), 1000);
    return () => clearInterval(id);
  }, [clock]);

  const { decimals, rewardVaultBalance } = info;
  const claimable = claimableUnits(position, now);
  const locked = position.principal > 0n && now < position.unlockAt;
  const rewardsCovered = rewardVaultBalance >= claimable;
  const busy = claim.isLoading || unstake.isLoading;
  const fmt = (v: bigint, max?: number) => `${formatUnits(v, decimals, max)} ${symbol}`;

  const run = async (action: Promise<string>, done: string) => {
    try {
      await action;
      enqueueSnackbar(done, { variant: 'success' });
      setUnstakeOpen(false);
      setUnstakeInput('');
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : String(err), { variant: 'error' });
    }
  };

  const unstakeAmount = unstakeInput === '' ? null : toUnits(unstakeInput, decimals);
  const remainder = unstakeAmount === null ? 0n : position.principal - unstakeAmount;
  const unstakeProblem =
    unstakeAmount !== null && (unstakeAmount <= 0n || unstakeAmount > position.principal)
      ? t.errExceedsPrincipal
      : remainder > 0n && remainder < info.pool.minStake
      ? `${t.remainderBelowMinimum} ${fmt(info.pool.minStake)}`
      : null;

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <Typography variant="h6">{fmt(position.principal)}</Typography>
        <Chip
          size="small"
          color={locked ? 'default' : 'primary'}
          label={locked ? `${t.lockedUntil} ${formatDate(position.unlockAt)}` : t.unlocked}
        />
      </div>
      <Typography variant="body2" color="textSecondary">
        {formatBps(position.rateBps)} {t.apr} · {t.claimable}: {fmt(claimable, 6)} · {t.claimed}: {fmt(position.totalClaimed, 6)}
      </Typography>
      {position.principal === 0n && claimable > 0n && <Alert severity="info">{t.rewardsPendingFunding}</Alert>}
      {claimable > 0n && !rewardsCovered && <Alert severity="warning">{t.errInsufficientRewardVault}</Alert>}
      <div className={classes.actions}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={busy || claimable === 0n || !rewardsCovered}
          onClick={() => run(claim.mutateAsync(), `${t.claimed} ${fmt(claimable, 6)}`)}
        >
          {strings.claim}
        </Button>
        <Tooltip title={locked ? `${t.lockedUntil} ${formatDate(position.unlockAt)}` : ''}>
          <span>
            <Button variant="outlined" color="primary" size="small" disabled={busy || locked} onClick={() => setUnstakeOpen(true)}>
              {strings.unstake}
            </Button>
          </span>
        </Tooltip>
        <Button variant="outlined" color="primary" size="small" disabled={busy} onClick={() => setStakeOpen(true)}>
          {t.addMore}
        </Button>
      </div>

      <TokenStakeDialog open={stakeOpen} onClose={() => setStakeOpen(false)} mint={mint} symbol={symbol} />

      <DialogForm open={unstakeOpen} onClose={() => setUnstakeOpen(false)} fullWidth>
        <DialogTitle>
          {strings.unstake} {symbol}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={strings.amount}
            fullWidth
            variant="outlined"
            margin="normal"
            value={unstakeInput}
            placeholder={formatUnits(position.principal, decimals)}
            onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setUnstakeInput(e.target.value)}
            error={!!unstakeProblem}
            helperText={unstakeProblem ?? t.unstakeHint}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button onClick={() => setUnstakeInput('')}>{strings.max}</Button>
                  <Typography color="primary">{symbol}</Typography>
                </InputAdornment>
              ),
            }}
          />
          {claimable > 0n && (
            <Typography variant="body2" color="textSecondary">
              {rewardsCovered ? t.rewardsPaidWithUnstake : t.rewardsStayClaimable} ({fmt(claimable, 6)})
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnstakeOpen(false)} disabled={busy}>
            {strings.cancel}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={busy || !!unstakeProblem}
            onClick={() => run(unstake.mutateAsync(unstakeAmount), `${t.unstaked} ${fmt(unstakeAmount ?? position.principal)}`)}
          >
            {busy ? t.sending : strings.unstake}
          </Button>
        </DialogActions>
      </DialogForm>
    </div>
  );
}
